import { useCallback, useEffect, useRef, useState } from 'react'
import { BackHandler } from 'react-native'

import { useNavigation } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import { i18n } from '@zet/localization'
import { appVideos } from '@zet/utils/Constants'
import * as ScreenOrientation from 'expo-screen-orientation'
import _ from 'lodash'
import { YoutubeIframeRef } from 'react-native-youtube-iframe'

interface useTrainingVideoPlayerProps {
  isLoadingVideo?: boolean
  training_name?: string
  youtube_video_details?: any
  isGenericVideoPlayer?: boolean
  videoId?: string
}

const useTrainingVideoPlayer = ({
  isLoadingVideo,
  training_name,
  youtube_video_details,
  isGenericVideoPlayer,
  videoId,
}: useTrainingVideoPlayerProps) => {
  const [isLoading, setIsLoading] = useState(isLoadingVideo)
  const [isFullScreenLoaded, setIsFullScreenLoaded] = useState(false)
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(i18n._locale)
  const [selectedLanguageVideo, setSelectedLanguageVideo] = useState({})

  const playerRef = useRef<YoutubeIframeRef>()
  const navigation = useNavigation()

  useEffect(() => {
    if (isGenericVideoPlayer) {
      setSelectedLanguageVideo({
        languageId: selectedLanguage,
        videoId: videoId || youtube_video_details?.en?.videoId,
        isPortrait: true,
      })
    } else {
      setSelectedLanguageVideo(youtube_video_details[0])
    }
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      handleBackButton
    )
        return () => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT)
      subscription.remove()
    }
  }, [])

  useEffect(() => {
    if (isGenericVideoPlayer) {
      if (selectedLanguage && !_.isEmpty(youtube_video_details)) {
        if (
          Object.keys(youtube_video_details).length === 1 &&
          Object.keys(youtube_video_details).includes('hi')
        ) {
          setSelectedLanguageVideo({
            languageId: 'hi',
            videoId:
              youtube_video_details?.hi?.videoId ||
              youtube_video_details?.hi?.videoLink,
            isPortrait: true,
          })
        } else {
          setSelectedLanguageVideo({
            languageId: selectedLanguage,
            videoId:
              youtube_video_details[selectedLanguage]?.videoId ||
              youtube_video_details[selectedLanguage]?.videoLink,
            isPortrait: true,
          })
        }
      } else if (_.isEmpty(youtube_video_details) && videoId) {
        setSelectedLanguageVideo({
          languageId: selectedLanguage,
          videoId: videoId || appVideos.zetPlaceHolder.videoId,
          isPortrait: true,
        })
      }
    }
  }, [videoId, selectedLanguage, youtube_video_details])

  useEffect(() => {
    if (!isGenericVideoPlayer) {
      const video = youtube_video_details?.find(
        (item) => item.languageId === selectedLanguage
      )
      if (video) {
        setSelectedLanguageVideo(video)
      }
    }
  }, [selectedLanguage])

  useEffect(() => {
    setIsFullScreenLoaded(false)
    lockOrientation()
  }, [selectedLanguageVideo])

  const handleBackButton = () => {
    handleClose()
    return true
  }

  const handleBottomSheet = () => {
    setIsBottomSheetVisible(true)
  }

  const lockOrientation = async () => {
    if (!_.isEmpty(selectedLanguageVideo)) {
      await ScreenOrientation.lockAsync(
        selectedLanguageVideo?.isPortrait || isGenericVideoPlayer
          ? ScreenOrientation.OrientationLock.PORTRAIT
          : ScreenOrientation.OrientationLock.LANDSCAPE_RIGHT
      ).then(() => {
        setIsFullScreenLoaded(true)
      })
    }
  }

  const handleClose = async () => {
    try {
      await ScreenOrientation.lockAsync(
        ScreenOrientation.OrientationLock.PORTRAIT
      )
      setIsFullScreenLoaded(false)
      navigation.goBack()
    } catch (err) {
      console.log(err)
      // TODO: Check this one.
      // if (Platform.OS !== 'web') {
      //   const Sentry = await import('@sentry/react-native')
      //   Sentry.captureException(err)
      // }
    }
  }

  const onVideoReady = () => {
    setIsLoading(false)
  }

  const onStateChange = useCallback((state: string) => {
    if (state === 'ended' && !isGenericVideoPlayer) {
      publishEvent('click_action', {
        item_name: 'RecordedTraningCompleted',
        training_name,
      })
    }
  }, [])

  return {
    playerRef,
    isLoading,
    isFullScreenLoaded,
    isBottomSheetVisible,
    selectedLanguage,
    selectedLanguageVideo,
    handleClose,
    onVideoReady,
    onStateChange,
    handleBottomSheet,
    setSelectedLanguage,
    setIsBottomSheetVisible,
  }
}

export default useTrainingVideoPlayer